/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, RefObject } from 'preact';
import type { Keys } from '#utils/UNSAFE_keys';
import type { ItemContext } from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  CurrentRowVariant,
  TableNoDataRendererContext,
  TableRowContext,
  TableSelectionDetail
} from '#UNSAFE_TableView';
import type { ActiveCell, TableColumn, ViewportInfo } from './Table';

import { Fragment } from 'preact';
import { useCallback } from 'preact/hooks';
import { containsKey } from '#utils/UNSAFE_keys';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { LoadMoreCollection } from '#PRIVATE_LoadMoreCollection';
import { backgroundStyles } from './themes/Table.css';
import { TableNoData } from './TableNoData';
import { MemoizeTableRow } from './TableRow';
import { TableSkeletonRow } from './TableSkeletonRow';

/**
 * Props for the TableBody Component
 */
type TableBodyProps<K extends string | number, D, C extends string> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  tableId: string;
  isValidRender: boolean;
  getAccessibleRowHeaders: (context: TableRowContext<K, D>) => Set<C>;
  tableData: CollectionData<K, D>[];
  columnsArray: TableColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  hasFooters: boolean;
  hasVerticalUnderflow: boolean;
  isShowFocusRing: boolean;
  isTabbableMode: boolean;
  hasMore: boolean;
  isRtl: boolean;
  isEmbedded: boolean;
  isHighContrast: boolean;
  onLoadMore: () => void;
  activeCell?: ActiveCell<K, C>;
  currentRowKey?: K;
  currentRowVariant?: CurrentRowVariant;
  selected: { row?: Keys<K>; column?: Keys<C> };
  isRowSelectionEnabled: boolean;
  isSingleRowSelectionEnabled: boolean;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  noDataRenderer?: (context: TableNoDataRendererContext) => ComponentChildren;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
  disabledInteractionKeys?: ('enter' | 'space')[];
};

/**
 * The internal component used to render the tbody of a Table.
 */
export function TableBody<K extends string | number, D, C extends string>({
  rootRef,
  viewportInfo,
  tableId,
  isValidRender,
  getAccessibleRowHeaders,
  tableData,
  columnsArray,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  hasFooters,
  hasVerticalUnderflow,
  isShowFocusRing,
  isTabbableMode,
  hasMore,
  onLoadMore,
  activeCell,
  currentRowKey,
  currentRowVariant,
  selected,
  isRowSelectionEnabled,
  isSingleRowSelectionEnabled,
  disabledInteractionKeys,
  onSelectionChange,
  noDataRenderer,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  isRtl,
  isEmbedded,
  isHighContrast
}: TableBodyProps<K, D, C>) {
  const browser = getClientHints().browser;

  // returns a TableRowContext based on ItemContext (added key)
  const getRowContext = useCallback((context: ItemContext<CollectionData<K, D>>) => {
    return {
      index: context.index,
      data: context.data.data,
      key: context.data.metadata.key
    };
  }, []);

  let isPreviousSelected = false;
  const collectionChildrenFunc = (context: ItemContext<CollectionData<K, D>>) => {
    const tableRowContext = getRowContext(context);
    const rowKey = tableRowContext.key;
    const containsActiveCell = activeCell?.rowKey === rowKey;
    const activeColumnKey = containsActiveCell ? activeCell.columnKey : undefined;
    const isSelected = containsKey(selected.row, rowKey);
    const rowProps = {
      tableId,
      columnsArray,
      rowHeaderColumnKeys: getAccessibleRowHeaders(tableRowContext),
      hasVerticalGridlines,
      hasHorizontalGridlines,
      hasFooters,
      hasVerticalUnderflow,
      hasMore,
      isSelected,
      isPreviousSelected,
      disabledInteractionKeys,
      onSelectionChange,
      isRowSelectionEnabled,
      isSingleRowSelectionEnabled,
      variant: currentRowKey === rowKey ? currentRowVariant : 'none',
      isShowFocusRing: isShowFocusRing && containsActiveCell,
      activeColumnKey,
      isTabbableMode,
      selectedRowKeys: selected.row,
      selectedColumnKeys: selected.column,
      rowIndex: tableRowContext.index,
      rowKey,
      rowData: tableRowContext.data,
      isFinalRow: tableRowContext.index === tableData.length - 1,
      startStickyEdge,
      endStickyEdge,
      firstStickyColumnIndex: firstStickyColumnIndex,
      finalStickyColumnIndex: finalStickyColumnIndex,
      columnBorderEdges,
      isRtl,
      isHighContrast
    };
    isPreviousSelected = isSelected;
    return <MemoizeTableRow {...rowProps} />;
  };

  const getNoDataProps = () => {
    const isActive = activeCell?.type === 'noData';
    const hasTabIndex = isActive && !isTabbableMode;
    return {
      rootRef,
      tableId,
      columnsCount: columnsArray.length,
      isShowFocusRing: isShowFocusRing && isActive,
      hasTabIndex,
      isTabbableMode,
      ariaRowIndex: isValidRender ? 2 : 1,
      renderer: isValidRender ? noDataRenderer : undefined
    };
  };

  const isShowEmptyLoading = tableData.length === 0 && hasMore;
  const isShowNoData = tableData.length === 0 && !hasMore;

  return (
    <tbody class={backgroundStyles.inherit} role={'presentation'} data-oj-table-body={tableId}>
      {isShowNoData || !isValidRender ? (
        <TableNoData {...getNoDataProps()} />
      ) : (
        <Fragment>
          <LoadMoreCollection<CollectionData<K, D>>
            data={tableData}
            loadMoreIndicator={
              <TableSkeletonRow
                tableId={tableId}
                hasTabIndex={
                  isShowEmptyLoading
                    ? activeCell?.type === 'loading'
                    : activeCell?.type === 'loadMore'
                }
                colspan={columnsArray.length}
                isLoadMore={!isShowEmptyLoading}
                isShowFocusRing={
                  isShowFocusRing &&
                  (isShowEmptyLoading
                    ? activeCell?.type === 'loading'
                    : activeCell?.type === 'loadMore')
                }
                isEmbedded={isEmbedded}
                isHighContrast={isHighContrast}
              />
            }
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            viewportConfig={viewportInfo.viewportConfig}>
            {collectionChildrenFunc}
          </LoadMoreCollection>
          <tr
            style={browser !== 'firefox' ? 'height: 100%;' : undefined}
            role={'presentation'}
            data-oj-table-spacer={tableId}
          />
        </Fragment>
      )}
    </tbody>
  );
}
